import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-background-200/70">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left cursor-pointer group"
      >
        <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4 group-hover:text-accent-600 transition-colors">
          {question}
        </span>
        <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-45' : ''}`}>
          <i className="ri-add-line text-foreground-500"></i>
        </span>
      </button>
      {open && (
        <div className="pb-4">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function AboutFAQSection() {
  const { t } = useTranslation('home');

  const faqs = [
    { q: t('faq_q1'), a: t('faq_a1') },
    { q: t('faq_q2'), a: t('faq_a2') },
    { q: t('faq_q3'), a: t('faq_a3') },
    { q: t('faq_q4'), a: t('faq_a4') },
    { q: t('faq_q5'), a: t('faq_a5') },
    { q: t('faq_q6'), a: t('faq_a6') },
    { q: t('faq_q7'), a: t('faq_a7') },
    { q: t('faq_q8'), a: t('faq_a8') },
  ];

  return (
    <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="flex-1">
            <span className="text-xs text-foreground-400 uppercase tracking-widest">{t('about_label')}</span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mt-3 mb-6 leading-tight">
              {t('about_title')}
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>{t('about_para1')}</p>
              <p>{t('about_para2')}</p>
            </div>

            <div className="mt-8 flex gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20photography%20of%20international%20shopping%20essentials%20including%20a%20world%20map%20a%20tablet%20showing%20shopping%20sites%20shipping%20labels%20and%20a%20magnifying%20glass%20on%20a%20warm%20cork%20board%20surface%20soft%20morning%20daylight%20cozy%20research%20aesthetic%20warm%20honey%20and%20tan%20tones%20organized%20yet%20lived%20in%20feel%20editorial%20planning%20photography&width=600&height=400&seq=about-img-1-v7&orientation=landscape"
                alt="CSSBuy Spreadsheet shopping guide"
                title="CSSBuy Spreadsheet - Guia de compras"
                className="rounded-xl w-[40%] object-cover"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Artistic%20unboxing%20scene%20of%20a%20premium%20cardboard%20shipping%20box%20opened%20on%20a%20light%20wooden%20floor%20with%20white%20tissue%20paper%20spilling%20out%20and%20a%20pair%20of%20stylish%20sneakers%20partially%20revealed%20beside%20the%20box%20warm%20afternoon%20sunlight%20streaming%20through%20nearby%20window%20creating%20cozy%20anticipation%20mood%20lifestyle%20editorial%20photography%20soft%20cream%20and%20warm%20sand%20tones%20genuine%20unboxing%20moment&width=800&height=400&seq=about-img-2-v7&orientation=landscape"
                alt="CSSBuy Spreadsheet unboxing"
                title="CSSBuy Spreadsheet - Produtos verificados"
                className="rounded-xl w-[60%] object-cover"
              />
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              {t('faq_title')}
            </h3>
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              {faqs.map((faq, idx) => (
                <FAQItem key={idx} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}